import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { DownloadDialogComponent } from '@shared/components/download-dialog/download-dialog.component';
import { DownloadStatus } from '@shared/models/download-status.model';
import { SharedState } from '../store/shared.state';
import { ClearDownloadProgress } from '../store/shared.actions';
import { take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DownloadDialogManagerService {
  @Select(SharedState.getDownloadProgress) downloadProgress$: Observable<{ [downloadType: string]: number }>;
  @Select(SharedState.getDownloadFileNames) downloadFileNames$: Observable<{ [downloadType: string]: string }>;

  private downloadDialogRefs: Map<string, MatDialogRef<DownloadDialogComponent>> = new Map();
  private fileNames: Map<string, string> = new Map();
  private expandedStates: Map<string, boolean> = new Map();
  private lastProgress: Map<string, number> = new Map();

  // Height constants for positioning
  private readonly collapsedHeight = 65;
  private readonly expandedCompleteHeight = 130;
  private readonly expandedDownloadingHeight = 190;
  private readonly gap = 20;
  private readonly topMargin = 50;

  constructor(private dialog: MatDialog, private ngZone: NgZone, private store: Store) {}

  public init(): void {
    // Subscribe to custom filenames from state
    this.downloadFileNames$.subscribe((customFileNames: { [downloadType: string]: string }) => {
      this.ngZone.run(() => {
        // Update local fileNames map with custom filenames from state
        Object.keys(customFileNames).forEach((downloadType) => {
          if (customFileNames[downloadType]) {
            this.fileNames.set(downloadType, customFileNames[downloadType]);
          }
        });
      });
    });

    // Subscribe to progress changes - this service stays alive across navigation
    this.downloadProgress$.subscribe((progressMap: { [downloadType: string]: number }) => {
      this.ngZone.run(() => {
        this.handleProgressUpdate(progressMap);
      });
    });
  }

  private handleProgressUpdate(progressMap: { [downloadType: string]: number }): void {
    // Iterate through all download types
    Object.keys(progressMap).forEach((downloadType) => {
      const progress = progressMap[downloadType];
      const lastProgress = this.lastProgress.get(downloadType) || 0;

      // Ignore stale/backwards progress updates from Firebase (except for 0 and 100)
      if (progress < lastProgress && progress !== 0 && progress !== 100) {
        return;
      }

      // Update tracked progress
      this.lastProgress.set(downloadType, progress);

      // Generate filename for this download type if not already generated
      if (!this.fileNames.has(downloadType)) {
        this.fileNames.set(downloadType, this.generateFormattedFilename(downloadType));
      }
      const fileName = this.fileNames.get(downloadType);

      if (progress >= 0 && progress < 100) {
        // Open dialog if not already open for this download type
        if (!this.downloadDialogRefs.has(downloadType)) {
          this.openDownloadDialog(downloadType);
        }
        // Update dialog data with progress
        const dialogRef = this.downloadDialogRefs.get(downloadType);
        if (dialogRef) {
          dialogRef.componentInstance.updateData({
            isDownloading: true,
            isComplete: false,
            filename: fileName,
            progress,
            hasFailed: false,
            downloadType,
            onExpandStateChange: (type: string, isExpanded: boolean) => this.updateDialogState(type, isExpanded),
            operationType: downloadType === 'bulk-upload' ? 'upload' : 'download'
          });
        }
      } else if (progress === 100) {
        // Update to complete state
        const dialogRef = this.downloadDialogRefs.get(downloadType);
        if (dialogRef) {
          dialogRef.componentInstance.updateData({
            isDownloading: false,
            isComplete: true,
            filename: fileName,
            progress: 100,
            hasFailed: false,
            downloadType,
            onExpandStateChange: (type: string, isExpanded: boolean) => this.updateDialogState(type, isExpanded),
            operationType: downloadType === 'bulk-upload' ? 'upload' : 'download'
          });
          // Reposition all dialogs after a short delay to allow DOM to update
          setTimeout(() => {
            this.repositionAllDialogs();
          }, 100);
        }
      }
    });
  }

  public updateDialogState(downloadType: string, isExpanded: boolean): void {
    this.expandedStates.set(downloadType, isExpanded);
    // Use a small delay to ensure DOM has updated with animation
    setTimeout(() => {
      this.repositionAllDialogs();
    }, 50);
  }

  private repositionAllDialogs(): void {
    let cumulativeTop = this.topMargin;

    // Iterate through dialogs in order they were created
    this.downloadDialogRefs.forEach((dialogRef, downloadType) => {
      // Update the dialog position
      dialogRef.updatePosition({
        top: `${cumulativeTop}px`,
        right: '20px'
      });

      // Calculate height for this dialog based on its expanded state and completion status
      const isExpanded = this.expandedStates.get(downloadType) ?? true; // Default to expanded
      let dialogHeight = this.collapsedHeight;

      if (isExpanded) {
        // Check if dialog is complete or still downloading
        const isComplete = dialogRef.componentInstance.data?.isComplete ?? false;
        dialogHeight = isComplete ? this.expandedCompleteHeight : this.expandedDownloadingHeight;
      }

      // Add to cumulative top for next dialog
      cumulativeTop += dialogHeight + this.gap;
    });
  }

  private openDownloadDialog(downloadType: string): void {
    // Use custom filename if available, otherwise generate default
    let fileName = this.fileNames.get(downloadType);
    if (!fileName) {
      fileName = this.generateFormattedFilename(downloadType);
      this.fileNames.set(downloadType, fileName);
    }

    // Initialize as expanded by default
    this.expandedStates.set(downloadType, true);

    // Calculate initial vertical position dynamically
    let topPosition = this.topMargin;
    this.downloadDialogRefs.forEach((existingDialogRef, type) => {
      const isExpanded = this.expandedStates.get(type) ?? true;
      let height = this.collapsedHeight;

      if (isExpanded) {
        const isComplete = existingDialogRef.componentInstance.data?.isComplete ?? false;
        height = isComplete ? this.expandedCompleteHeight : this.expandedDownloadingHeight;
      }

      topPosition += height + this.gap;
    });

    const dialogRef = this.dialog.open(DownloadDialogComponent, {
      maxWidth: '480px',
      position: {
        top: `${topPosition}px`,
        right: '20px'
      },
      hasBackdrop: false,
      disableClose: true,
      panelClass: 'download-dialog-container',
      data: {
        isDownloading: true,
        isComplete: false,
        filename: fileName,
        progress: 0,
        hasFailed: false,
        downloadType,
        onExpandStateChange: (type: string, isExpanded: boolean) => this.updateDialogState(type, isExpanded),
        operationType: downloadType === 'bulk-upload' ? 'upload' : 'download'
      } as DownloadStatus
    });

    // Store the dialog reference
    this.downloadDialogRefs.set(downloadType, dialogRef);

    // Handle dialog close
    dialogRef.afterClosed().subscribe(() => {
      this.downloadDialogRefs.delete(downloadType);
      this.fileNames.delete(downloadType);
      this.expandedStates.delete(downloadType);
      this.lastProgress.delete(downloadType);
      
      // Clear the download progress from state to prevent stale data
      this.store.dispatch(new ClearDownloadProgress(downloadType));
      
      // Reposition remaining dialogs after one is closed
      this.repositionAllDialogs();
      // Remove focus from the download button to prevent grey background
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  }

  private generateFormattedFilename(prefix: string): string {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12

    return `${prefix}-${year}-${month}-${day}-${hours}.${minutes}${ampm}.xlsx`;
  }
}
